import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart3,
  Users,
  Eye,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  TrendingUp,
  TrendingDown,
  Clock,
  RefreshCw,
  ArrowUpRight,
  Loader2,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [overview, setOverview] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [events, setEvents] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, dailyRes, pagesRes, sourcesRes, devicesRes, realtimeRes, eventsRes] = await Promise.all([
        api.get(`/analytics/overview?days=${period}`),
        api.get(`/analytics/pageviews/daily?days=${period}`),
        api.get(`/analytics/pages/top?days=${period}`),
        api.get(`/analytics/sources?days=${period}`),
        api.get(`/analytics/devices?days=${period}`),
        api.get('/analytics/realtime'),
        api.get(`/analytics/events?days=${period}`)
      ]);

      setOverview(overviewRes.data);
      setDailyData(dailyRes.data.data || []);
      setTopPages(pagesRes.data.data || []);
      setSources(sourcesRes.data.data || []);
      setDevices(devicesRes.data);
      setRealtime(realtimeRes.data);
      setEvents(eventsRes.data.data || []);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh realtime every 30 seconds
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/analytics/realtime');
        setRealtime(res.data);
      } catch (e) {}
    }, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const getDeviceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const getSourceColor = (medium) => {
    switch (medium) {
      case 'organic': return 'bg-green-100 text-green-700';
      case 'social': return 'bg-blue-100 text-blue-700';
      case 'referral': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxHeight = 120 }) => {
    if (!data || data.length === 0) return null;
    const maxValue = Math.max(...data.map(d => d.pageviews || 0), 1);
    
    return (
      <div className="flex items-end gap-1 h-32">
        {data.slice(-30).map((item, idx) => (
          <div
            key={idx}
            className="flex-1 bg-orange-500 rounded-t hover:bg-orange-600 transition-colors cursor-pointer group relative"
            style={{ height: `${((item.pageviews || 0) / maxValue) * maxHeight}px`, minHeight: '4px' }}
            title={`${item.date}: ${item.pageviews} vues, ${item.visitors} visiteurs`}
          >
            <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap z-10">
              {item.date}<br/>{item.pageviews} vues
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Analytics</h1>
          <p className="text-slate-500">Statistiques de fréquentation du site web</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
              <SelectItem value="365">12 derniers mois</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Realtime Card */}
      {realtime && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Activity className="w-5 h-5 text-green-600" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">En temps réel</p>
                  <p className="text-2xl font-bold text-green-700">{realtime.active_visitors_5min} visiteur{realtime.active_visitors_5min > 1 ? 's' : ''} actif{realtime.active_visitors_5min > 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="text-right text-sm text-slate-500">
                <p>{realtime.active_visitors_30min} visiteurs (30 min)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pages vues</p>
                <p className="text-2xl font-bold text-slate-800">{overview?.total_pageviews?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Eye className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            {overview?.today && (
              <div className="mt-2 flex items-center text-sm">
                {overview.today.pageviews_change >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={overview.today.pageviews_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {overview.today.pageviews_change >= 0 ? '+' : ''}{overview.today.pageviews_change}
                </span>
                <span className="text-slate-400 ml-1">vs hier</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Visiteurs uniques</p>
                <p className="text-2xl font-bold text-slate-800">{overview?.unique_visitors?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            {overview?.today && (
              <div className="mt-2 flex items-center text-sm">
                {overview.today.visitors_change >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span className={overview.today.visitors_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {overview.today.visitors_change >= 0 ? '+' : ''}{overview.today.visitors_change}
                </span>
                <span className="text-slate-400 ml-1">vs hier</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Sessions</p>
                <p className="text-2xl font-bold text-slate-800">{overview?.unique_sessions?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              ~{overview?.avg_pages_per_session || 0} pages/session
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Aujourd'hui</p>
                <p className="text-2xl font-bold text-slate-800">{overview?.today?.pageviews || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {overview?.today?.visitors || 0} visiteurs
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Évolution du trafic</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyData.length > 0 ? (
            <SimpleBarChart data={dailyData} />
          ) : (
            <div className="h-32 flex items-center justify-center text-slate-400">
              Aucune donnée disponible
            </div>
          )}
          <div className="flex justify-between text-xs text-slate-400 mt-2">
            <span>{dailyData[0]?.date || ''}</span>
            <span>{dailyData[dailyData.length - 1]?.date || ''}</span>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for detailed stats */}
      <Tabs defaultValue="pages" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pages">Pages populaires</TabsTrigger>
          <TabsTrigger value="sources">Sources de trafic</TabsTrigger>
          <TabsTrigger value="devices">Appareils</TabsTrigger>
          <TabsTrigger value="events">Événements</TabsTrigger>
        </TabsList>

        <TabsContent value="pages">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pages les plus visitées</CardTitle>
            </CardHeader>
            <CardContent>
              {topPages.length > 0 ? (
                <div className="space-y-3">
                  {topPages.map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-400 w-6">{idx + 1}</span>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{page.page_path}</p>
                          {page.title && <p className="text-sm text-slate-500 truncate">{page.title}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{page.pageviews}</p>
                          <p className="text-slate-400">vues</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{page.visitors}</p>
                          <p className="text-slate-400">visiteurs</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sources de trafic</CardTitle>
            </CardHeader>
            <CardContent>
              {sources.length > 0 ? (
                <div className="space-y-3">
                  {sources.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Globe className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-800 capitalize">{source.source}</p>
                          <Badge className={getSourceColor(source.medium)} variant="secondary">
                            {source.medium}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{source.visitors}</p>
                          <p className="text-slate-400">visiteurs</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{source.pageviews}</p>
                          <p className="text-slate-400">vues</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Device Types */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Type d'appareil</CardTitle>
              </CardHeader>
              <CardContent>
                {devices?.devices?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.devices.map((device, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(device.device_type)}
                          <span className="text-sm">{device.device_type}</span>
                        </div>
                        <span className="font-medium">{device.visitors}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucune donnée</p>
                )}
              </CardContent>
            </Card>

            {/* Browsers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Navigateurs</CardTitle>
              </CardHeader>
              <CardContent>
                {devices?.browsers?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.browsers.slice(0, 5).map((browser, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm truncate">{browser.browser}</span>
                        <span className="font-medium">{browser.visitors}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucune donnée</p>
                )}
              </CardContent>
            </Card>

            {/* OS */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Systèmes d'exploitation</CardTitle>
              </CardHeader>
              <CardContent>
                {devices?.operating_systems?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.operating_systems.slice(0, 5).map((os, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm truncate">{os.os}</span>
                        <span className="font-medium">{os.visitors}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucune donnée</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Événements & Conversions</CardTitle>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{event.event_name}</p>
                        {event.event_category && (
                          <Badge variant="outline" className="mt-1">{event.event_category}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{event.count}</p>
                          <p className="text-slate-400">total</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-800">{event.unique_visitors}</p>
                          <p className="text-slate-400">uniques</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400">Aucun événement enregistré</p>
                  <p className="text-sm text-slate-400 mt-1">Les événements (clics, soumissions de formulaires) apparaîtront ici</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
